import {
  ActionIcon,
  Box,
  Button,
  Group,
  Image,
  NativeSelect,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'

import { IconTrash } from '@/components/icons'
import { ActionButton, AppModal, InlineLoader } from '@/components/ui'
import { identifierTypeLabel } from '@/lib/identifier-labels'
import type { DisplayIdent } from '@/components/tickets/ticket-detail-page-helpers'
import type { ProjectRow, ProjectUserIdentifierRow } from '@/types/api'

type TicketCustomerIdentityModalsProps = {
  avatarOpened: boolean
  avatarSrc: string | null
  onCloseAvatar: () => void
  identifiersOpened: boolean
  identifiers: DisplayIdent[]
  customerIdentifierRows: ProjectUserIdentifierRow[]
  workspaceProjects: ProjectRow[]
  newType: string
  newValue: string
  newDisplay: string
  saving: boolean
  onCloseIdentifiers: () => void
  onChangeNewType: (value: string) => void
  onChangeNewValue: (value: string) => void
  onChangeNewDisplay: (value: string) => void
  onSubmitNewIdentifier: () => void
  onDeleteIdentifier: (id: string) => void
}

export function TicketCustomerIdentityModals({
  avatarOpened,
  avatarSrc,
  onCloseAvatar,
  identifiersOpened,
  identifiers,
  customerIdentifierRows,
  workspaceProjects,
  newType,
  newValue,
  newDisplay,
  saving,
  onCloseIdentifiers,
  onChangeNewType,
  onChangeNewValue,
  onChangeNewDisplay,
  onSubmitNewIdentifier,
  onDeleteIdentifier,
}: TicketCustomerIdentityModalsProps) {
  return (
    <>
      <AppModal opened={avatarOpened} onClose={onCloseAvatar} title="Фото клиента">
        {avatarSrc ? (
          <Image src={avatarSrc} alt="" fit="contain" mah={480} mx="auto" />
        ) : (
          <Text size="sm" c="dimmed">
            Нет фото
          </Text>
        )}
      </AppModal>

      <AppModal
        opened={identifiersOpened}
        onClose={onCloseIdentifiers}
        title="Данные клиента"
        size="lg"
        footer={<ActionButton action="close" onClick={onCloseIdentifiers} />}
      >
        <Stack gap="md">
          <Text size="xs" c="dimmed">
            Идентификаторы клиента в этом проекте.
          </Text>
          <Stack gap="sm" style={{ maxHeight: 280, overflowY: 'auto' }}>
            {identifiers.length === 0 ? (
              <Text size="xs" c="dimmed">
                Нет записей
              </Text>
            ) : (
              identifiers.map((row, idx) => (
                <Group
                  key={row.id || `${row.identifier_type}-${row.identifier_value}-${idx}`}
                  justify="space-between"
                  wrap="nowrap"
                  align="flex-start"
                  gap="sm"
                >
                  <Box style={{ minWidth: 0 }}>
                    <Text size="xs" fw={600}>
                      {identifierTypeLabel(row.identifier_type)}
                    </Text>
                    <Text size="xs" style={{ wordBreak: 'break-all' }}>
                      {row.identifier_value}
                    </Text>
                    {(() => {
                      const projectId = row.id
                        ? customerIdentifierRows.find((x) => x.id === row.id)?.project_id
                        : undefined
                      const projectName = projectId
                        ? workspaceProjects.find((project) => project.id === projectId)?.name || projectId
                        : ''
                      return projectName ? (
                        <Text size="xs" c="dimmed">
                          Проект: {projectName}
                        </Text>
                      ) : null
                    })()}
                    {row.display_name ? (
                      <Text size="xs" c="dimmed">
                        {row.display_name}
                      </Text>
                    ) : null}
                  </Box>
                  {row.id ? (
                    <Tooltip label="Удалить" withArrow>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        aria-label="Удалить идентификатор"
                        onClick={() =>
                          openConfirmModal({
                            title: 'Удалить идентификатор?',
                            children: (
                              <Stack gap={6}>
                                <Text size="sm" c="dimmed">
                                  Запись будет удалена из данных клиента в этом проекте.
                                </Text>
                                <Text size="sm">
                                  <Text component="span" fw={600}>
                                    {identifierTypeLabel(row.identifier_type)}
                                  </Text>
                                  : {row.identifier_value}
                                </Text>
                              </Stack>
                            ),
                            labels: { confirm: 'Удалить', cancel: 'Отмена' },
                            confirmProps: { color: 'red' },
                            onConfirm: () => onDeleteIdentifier(row.id || ''),
                          })
                        }
                      >
                        <IconTrash size={16} stroke={1.5} />
                      </ActionIcon>
                    </Tooltip>
                  ) : (
                    <Box w={28} />
                  )}
                </Group>
              ))
            )}
          </Stack>
          <Text fw={600} size="xs">
            Добавить
          </Text>
          <NativeSelect
            size="xs"
            label="Тип"
            value={newType}
            onChange={(e) => onChangeNewType(e.currentTarget.value)}
            data={[
              { value: 'email', label: 'Email' },
              { value: 'telegram_id', label: 'Telegram ID' },
              { value: 'telegram_username', label: 'Telegram (ник)' },
              { value: 'phone', label: 'Телефон' },
              { value: 'whatsapp_number', label: 'WhatsApp' },
              { value: 'user_id', label: 'ID пользователя' },
              { value: 'device_id', label: 'Device ID' },
              { value: 'widget_user_id', label: 'ID пользователя (виджет)' },
              { value: 'custom', label: 'Другое' },
            ]}
          />
          {newType === 'custom' ? (
            <TextInput
              size="xs"
              label="Подпись"
              value={newDisplay}
              onChange={(e) => onChangeNewDisplay(e.currentTarget.value)}
            />
          ) : null}
          <TextInput
            size="xs"
            label="Значение"
            value={newValue}
            onChange={(e) => onChangeNewValue(e.currentTarget.value)}
            disabled={saving}
          />
          {saving ? <InlineLoader label="Сохраняем данные клиента..." /> : null}
          <Button
            size="xs"
            loading={saving}
            disabled={saving}
            onClick={() => {
              const value = newValue.trim()
              if (!value) {
                showNotification({
                  title: 'Значение',
                  message: 'Укажите значение идентификатора.',
                  color: 'yellow',
                })
                return
              }
              const typeLabel =
                newType === 'custom'
                  ? newDisplay.trim() || 'Другое'
                  : identifierTypeLabel(newType)
              openConfirmModal({
                title: 'Добавить идентификатор?',
                children: (
                  <Text size="sm" c="dimmed">
                    Тип: {typeLabel}. Значение: {value}
                  </Text>
                ),
                labels: { confirm: 'Добавить', cancel: 'Отмена' },
                onConfirm: onSubmitNewIdentifier,
              })
            }}
          >
            Добавить
          </Button>
        </Stack>
      </AppModal>
    </>
  )
}
